/**
 * Versions modal (Phase 4c). Lists the editor's in-memory published versions
 * with per-version actions:
 *
 *   - Preview this version → launches the runtime against the snapshot
 *     (instead of the draft) via the postMessage handshake.
 *   - Restore as draft → clones the published version's scenario into the
 *     working draft. Doesn't delete the published version.
 *   - Export JSON → downloads the published version's scenario JSON, which
 *     imports cleanly into any other Branchworks editor.
 *   - Delete → removes the version from the in-memory list. (Future:
 *     refuse to delete versions referenced by ongoing learner sessions.)
 *
 * Without persistence (Phase 5), the list is per-browser-session. Refreshing
 * the editor loses every published version. The Export action lets authors
 * keep durable copies on disk in the meantime.
 */
import { Download, RotateCcw, Play, Trash2 } from 'lucide-react';
import type { PublishedScenario } from '@branchworks/shared';
import { useScenarioStore } from '../store/scenarioStore';
import { Modal } from './Modal';
import { launchPreview } from '../preview/launchPreview';

export function VersionsModal() {
  const versions = useScenarioStore((s) => s.publishedVersions);
  const setOpenModal = useScenarioStore((s) => s.setOpenModal);
  const restoreFromPublished = useScenarioStore((s) => s.restoreFromPublished);
  const deletePublishedVersion = useScenarioStore((s) => s.deletePublishedVersion);

  return (
    <Modal
      kicker="Scenario history"
      title={`Published versions (${versions.length})`}
      onClose={() => setOpenModal(null)}
      maxWidth={720}
    >
      {versions.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {versions.map((v) => (
            <VersionRow
              key={v.versionTag}
              version={v}
              onPreview={() =>
                launchPreview(v.scenario, undefined, {
                  mode: 'published',
                  versionTag: v.versionTag,
                })
              }
              onRestore={() => {
                if (
                  confirm(
                    `Restore "${v.versionTag}" as the working draft? Your current draft will be replaced. (The published version stays in the list.)`,
                  )
                ) {
                  restoreFromPublished(v.versionTag);
                  setOpenModal(null);
                }
              }}
              onExport={() => exportVersion(v)}
              onDelete={() => {
                if (confirm(`Delete published version "${v.versionTag}"? This can't be undone.`)) {
                  deletePublishedVersion(v.versionTag);
                }
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function EmptyState() {
  return (
    <div className="text-sm italic text-center py-8 px-4 rounded border border-dashed border-cream-border text-ink-2">
      <div className="font-bold not-italic mb-1 text-ink">No published versions yet.</div>
      Use the <span className="not-italic font-bold">Publish</span> button in the toolbar to snapshot
      the current draft. Published versions live in this browser session only — Export each one to
      keep a durable copy on disk.
    </div>
  );
}

interface VersionRowProps {
  version: PublishedScenario;
  onPreview: () => void;
  onRestore: () => void;
  onExport: () => void;
  onDelete: () => void;
}

function VersionRow({ version, onPreview, onRestore, onExport, onDelete }: VersionRowProps) {
  const { versionTag, publishedAt, scenario } = version;
  const sceneCount = Object.keys(scenario.nodes || {}).length;
  const niceDate = formatPublishedAt(publishedAt);
  return (
    <div className="rounded border p-3 border-cream-border bg-cream-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className="font-bold text-ink font-mono">{versionTag}</span>
            <span className="text-xs text-ink-3">·</span>
            <span className="text-xs text-ink-2 truncate">{scenario.title}</span>
          </div>
          <div className="text-[11px] text-ink-3">
            Published {niceDate} · {sceneCount} scene{sceneCount === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onPreview}
            className="p-1.5 rounded hover:bg-cream text-ink-2"
            title={`Preview "${versionTag}" — launches the runtime against this snapshot`}
            aria-label={`Preview version ${versionTag}`}
          >
            <Play size={13} />
          </button>
          <button
            onClick={onRestore}
            className="p-1.5 rounded hover:bg-cream text-ink-2"
            title={`Restore "${versionTag}" as the working draft`}
            aria-label={`Restore version ${versionTag}`}
          >
            <RotateCcw size={13} />
          </button>
          <button
            onClick={onExport}
            className="p-1.5 rounded hover:bg-cream text-ink-2"
            title={`Export "${versionTag}" as JSON`}
            aria-label={`Export version ${versionTag}`}
          >
            <Download size={13} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-cream text-terracotta-dark"
            title={`Delete "${versionTag}" from the in-memory list`}
            aria-label={`Delete version ${versionTag}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function formatPublishedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function exportVersion(v: PublishedScenario): void {
  const blob = new Blob([JSON.stringify(v.scenario, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // Filename includes the version tag for clarity.
  const safeTitle = v.scenario.title.replace(/\s+/g, '_').toLowerCase();
  const safeTag = v.versionTag.replace(/\s+/g, '_').toLowerCase();
  a.download = `${safeTitle}_${safeTag}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
